import sanitizeHtmlLibrary from "sanitize-html";
import type sanitizeHtmlTypes from "sanitize-html";

// ADR-0034 §7.1.1 test #7 — XSS sanitize helper (allow-list, 2차 방어).
//
// Phase 1 = strict CSP (`script-src 'self'`) 가 1차 방어. 본 helper 는 렌더 직전
// 2차 방어로 위험 태그/속성/URL scheme 을 strip 한다.
//
// Tiptap 에디터/renderer 실측 allowlist:
// - StarterKit: p/h2-h4/ul/ol/li/blockquote/pre/code/hr/br + strong/em/s
// - editor 추가 mark: u/mark/span(style: color/background) + TextAlign(style)
// - media/table/custom nodes: img/figure/figcaption/table/thead/tbody/tfoot/tr/th/td/div
//   (thead/tfoot 는 이관된 원문 HTML 표에 실제로 쓰인다. allowlist 에서 빠져 있으면
//    sanitize-html 이 그 태그만 벗겨내 `<table><tr>…` 처럼 머리행이 섹션 밖으로
//    빠져나온 깨진 표가 되므로 안전한 구조 태그로 명시 허용한다.)
// - FAQ renderer: details/summary + rt-cms-* classes
// Embed iframe 은 sanitize 경로가 아니라 Tiptap JSON `renderEmbed()` 에서
// `isAllowedEmbedSrc` 로 검증 후 렌더된다. raw HTML sanitizer 에서는 iframe 전면 strip.

// URL 속성(href/src/…) — deny-list 가 아니라 **scheme allow-list** 로 판정한다.
// HTML entity / 제어문자 인코딩(`&#106;avascript:`, `java&#9;script:`,
// `jav&#x0d;ascript:`)은 regex 리터럴 매칭을 우회하므로, 값에서 entity 를 decode
// 하고 ASCII 공백/제어문자를 제거(브라우저 URL 파서와 동일 정규화)한 뒤 scheme 을
// 검사한다. sanitize-html parser 가 처리한 속성에도 동일 probe 를 적용한다.
const URL_ATTRS = new Set(["href", "src", "xlink:href"]);

const ALLOWED_URL_SCHEMES = new Set(["http", "https", "mailto", "tel"]);
const MALFORMED_DANGEROUS_TOKEN_RE =
  /<\/?[^>\s/]*(?:script|style|iframe|object|embed|svg|math|template)[^>\s/]*(?:\s[^>]*)?>[\s\S]*?<\/?[^>\s/]*(?:script|style|iframe|object|embed|svg|math|template)[^>\s/]*(?:\s[^>]*)?>/gi;
const SLASH_EVENT_ATTR_IN_URL_RE = /\/on[a-z]+=/i;
const ROOTTALE_CLASS_RE = /^rt-[a-z0-9_-]+$/;
const HEX_COLOR_RE = /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i;
const TEXT_ALIGN_RE = /^(left|right|center|justify)$/;
const ASPECT_RATIO_RE = /^\d+(?:\.\d+)?\s*\/\s*\d+(?:\.\d+)?$/;
const CSS_LENGTH_RE = /^(?:0|\d+(?:\.\d+)?(?:px|em|rem|%))$/i;
const CSS_LENGTH_LIST_RE =
  /^(?:0|\d+(?:\.\d+)?(?:px|em|rem|%))(?:\s+(?:0|\d+(?:\.\d+)?(?:px|em|rem|%))){0,3}$/i;
const CSS_LINE_HEIGHT_RE = /^(?:normal|\d+(?:\.\d+)?(?:px|em|rem|%)?)$/i;
const CSS_FONT_WEIGHT_RE = /^(?:normal|bold|[1-9]00)$/i;
const CSS_BORDER_RE =
  /^(?:none|0|(?:\d(?:\.\d+)?|[12]\d(?:\.\d+)?|3[0-2](?:\.0+)?)px\s+(?:solid|dashed|dotted)\s+#[0-9a-f]{3}(?:[0-9a-f]{3})?)$/i;
const CSS_TEXT_DECORATION_RE = /^(?:none|underline|line-through)$/i;

const CSS_MAGNITUDE_LIMITS: Record<string, Record<string, number>> = {
  "font-size": { "": 0, px: 96, em: 5, rem: 5, "%": 200 },
  "line-height": { "": 5, px: 96, em: 5, rem: 5, "%": 500 },
  margin: { "": 0, px: 2000, em: 20, rem: 20, "%": 200 },
  "margin-bottom": { "": 0, px: 2000, em: 20, rem: 20, "%": 200 },
  "margin-left": { "": 0, px: 2000, em: 20, rem: 20, "%": 200 },
  "margin-right": { "": 0, px: 2000, em: 20, rem: 20, "%": 200 },
  "margin-top": { "": 0, px: 2000, em: 20, rem: 20, "%": 200 },
  padding: { "": 0, px: 2000, em: 20, rem: 20, "%": 200 },
  "padding-bottom": { "": 0, px: 2000, em: 20, rem: 20, "%": 200 },
  "padding-left": { "": 0, px: 2000, em: 20, rem: 20, "%": 200 },
  "padding-right": { "": 0, px: 2000, em: 20, rem: 20, "%": 200 },
  "padding-top": { "": 0, px: 2000, em: 20, rem: 20, "%": 200 },
  width: { "": 0, px: 4000, em: 100, rem: 100, "%": 100 },
  "max-width": { "": 0, px: 4000, em: 100, rem: 100, "%": 100 },
  height: { "": 0, px: 4000, em: 100, rem: 100, "%": 100 },
};

function hasSafeCssMagnitude(property: string, value: string): boolean {
  const limits = CSS_MAGNITUDE_LIMITS[property];
  if (!limits) return true;
  const numericTokens = value.matchAll(/(\d+(?:\.\d+)?)(px|em|rem|%)?/gi);
  for (const match of numericTokens) {
    const unit = (match[2] ?? "").toLowerCase();
    const limit = limits[unit];
    if (limit === undefined || Number(match[1]) > limit) return false;
  }
  return true;
}

function filterUnsafeCssMagnitudes(style: string): string {
  return style
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .filter((declaration) => {
      const colon = declaration.indexOf(":");
      if (colon < 1) return false;
      const property = declaration.slice(0, colon).trim().toLowerCase();
      const value = declaration.slice(colon + 1).trim();
      return hasSafeCssMagnitude(property, value);
    })
    .join(";");
}

// scheme 구성에 악용되는 named entity 만 decode (`&colon;`/`&Tab;` 등).
const NAMED_ENTITY: Record<string, string> = {
  colon: ":",
  tab: "\t",
  newline: "\n",
  sol: "/",
  semi: ";",
  num: "#",
  period: ".",
  comma: ",",
  lpar: "(",
  rpar: ")",
};

function fromCodePointSafe(n: number): string {
  if (!Number.isFinite(n) || n < 0 || n > 0x10ffff) return "";
  try {
    return String.fromCodePoint(n);
  } catch {
    return "";
  }
}

/** URL 값 정규화 — entity decode + 공백/제어문자 제거 + 소문자 (scheme 판정용). */
function decodeUrlProbe(value: string): string {
  const decoded = value
    .replace(/&#x([0-9a-f]+);?/gi, (_, h) => fromCodePointSafe(parseInt(h, 16)))
    .replace(/&#(\d+);?/g, (_, d) => fromCodePointSafe(parseInt(d, 10)))
    .replace(/&([a-z]+);/gi, (m, name) => NAMED_ENTITY[name.toLowerCase()] ?? m);
  // 브라우저는 scheme 판정 시 ASCII 공백/제어문자(탭/개행/CR/NUL 포함)를 제거한다.
  return [...decoded].filter((c) => { const n = c.codePointAt(0) ?? 0; return n > 0x20 && n !== 0x7f; }).join("").toLowerCase();
}

function stripMalformedDangerousTokens(html: string): string {
  let out = html;
  for (let i = 0; i < 3; i++) {
    const next = out.replace(MALFORMED_DANGEROUS_TOKEN_RE, "");
    if (next === out) return out;
    out = next;
  }
  return out;
}

/**
 * 안전한 URL 값인지 — 상대경로/허용 scheme, data:image/* 는 img[src] 에서만(SVG 제외).
 */
function isSafeUrlValue(tag: string, attr: string, value: string): boolean {
  const probe = decodeUrlProbe(value);
  if (SLASH_EVENT_ATTR_IN_URL_RE.test(probe)) return false;
  const scheme = /^([a-z][a-z0-9+.-]*):/.exec(probe)?.[1];
  if (!scheme) return true; // 상대경로 / fragment / query
  if (ALLOWED_URL_SCHEMES.has(scheme)) return true;
  if (
    scheme === "data" &&
    tag === "img" &&
    attr === "src" &&
    probe.startsWith("data:image/")
  ) {
    // data:image/svg+xml 은 스크립트 실행 가능 → 차단.
    return !probe.startsWith("data:image/svg");
  }
  return false;
}

function sanitizeUrlAttributes(
  tagName: string,
  attribs: sanitizeHtmlTypes.Attributes,
): sanitizeHtmlTypes.Tag {
  const next: sanitizeHtmlTypes.Attributes = {};
  for (const [name, value] of Object.entries(attribs)) {
    const attr = name.toLowerCase();
    if (attr === "style") {
      const style = filterUnsafeCssMagnitudes(value);
      if (style) next[name] = style;
      continue;
    }
    next[name] = URL_ATTRS.has(attr) && !isSafeUrlValue(tagName, attr, value) ? "#" : value;
  }
  if (tagName === "a" && next.target === "_blank") {
    next.rel = "noopener noreferrer";
  }
  return { tagName, attribs: next };
}

const SANITIZE_OPTIONS: sanitizeHtmlTypes.IOptions = {
  allowedTags: [
    "a",
    "blockquote",
    "br",
    "cite",
    "code",
    "details",
    "div",
    "em",
    "figcaption",
    "figure",
    "h2",
    "h3",
    "h4",
    "hr",
    "img",
    "li",
    "mark",
    "ol",
    "p",
    "pre",
    "s",
    "section",
    "small",
    "span",
    "strong",
    "sub",
    "summary",
    "sup",
    "table",
    "tbody",
    "td",
    "tfoot",
    "th",
    "thead",
    "tr",
    "u",
    "ul",
  ],
  allowedAttributes: {
    "*": ["class", "data-*", "style"],
    a: [
      "href",
      "title",
      { name: "rel", multiple: true, values: ["noopener", "noreferrer"] },
      { name: "target", values: ["_blank"] },
    ],
    details: ["open", "class", "data-*"],
    div: ["class", "data-*", "style"],
    h2: ["id", "class", "data-*", "style"],
    h3: ["id", "class", "data-*", "style"],
    h4: ["id", "class", "data-*", "style"],
    img: ["src", "alt", "title", "width", "height", "loading", "class", "data-*"],
    mark: ["class", "data-*", "style"],
    p: ["class", "data-*", "style"],
    span: ["class", "data-*", "style"],
    td: ["colspan", "rowspan", "class", "data-*"],
    // scope 는 표 머리행의 접근성 의미(스크린리더 열/행 연결) — 값 집합을 고정해 허용.
    th: [
      "colspan",
      "rowspan",
      "class",
      "data-*",
      { name: "scope", values: ["col", "row", "colgroup", "rowgroup"] },
    ],
  },
  allowedClasses: {
    "*": [ROOTTALE_CLASS_RE],
  },
  allowedStyles: {
    img: {
      display: [/^block$/i],
      "margin-left": [CSS_LENGTH_RE, /^auto$/i],
      "margin-right": [CSS_LENGTH_RE, /^auto$/i],
    },
    figure: {
      display: [/^block$/i],
      "margin-left": [CSS_LENGTH_RE, /^auto$/i],
      "margin-right": [CSS_LENGTH_RE, /^auto$/i],
    },
    "*": {
      "aspect-ratio": [ASPECT_RATIO_RE],
      background: [HEX_COLOR_RE],
      "background-color": [HEX_COLOR_RE],
      border: [CSS_BORDER_RE],
      "border-bottom": [CSS_BORDER_RE],
      "border-left": [CSS_BORDER_RE],
      "border-right": [CSS_BORDER_RE],
      "border-top": [CSS_BORDER_RE],
      "border-collapse": [/^(?:collapse|separate)$/i],
      color: [HEX_COLOR_RE],
      "font-size": [CSS_LENGTH_RE],
      "font-weight": [CSS_FONT_WEIGHT_RE],
      height: [CSS_LENGTH_RE, /^auto$/i],
      "line-height": [CSS_LINE_HEIGHT_RE],
      margin: [CSS_LENGTH_LIST_RE],
      "margin-bottom": [CSS_LENGTH_RE],
      "margin-left": [CSS_LENGTH_RE],
      "margin-right": [CSS_LENGTH_RE],
      "margin-top": [CSS_LENGTH_RE],
      "max-width": [CSS_LENGTH_RE, /^none$/i],
      padding: [CSS_LENGTH_LIST_RE],
      "padding-bottom": [CSS_LENGTH_RE],
      "padding-left": [CSS_LENGTH_RE],
      "padding-right": [CSS_LENGTH_RE],
      "padding-top": [CSS_LENGTH_RE],
      "text-align": [TEXT_ALIGN_RE],
      "text-decoration": [CSS_TEXT_DECORATION_RE],
      width: [CSS_LENGTH_RE, /^auto$/i],
    },
  },
  allowedSchemes: ["http", "https", "mailto", "tel"],
  allowedSchemesByTag: {
    // tag별 목록은 전역 allowedSchemes를 덮어쓴다. data만 쓰면 정상적인
    // http/https 이미지 src까지 제거되므로 이미지가 사용할 전체 scheme을 명시한다.
    img: ["http", "https", "data"],
  },
  allowedSchemesAppliedToAttributes: ["href", "src", "xlink:href"],
  allowProtocolRelative: false,
  nonTextTags: [
    "script",
    "style",
    "textarea",
    "option",
    "iframe",
    "object",
    "embed",
    "svg",
    "math",
    "template",
  ],
  transformTags: {
    "*": sanitizeUrlAttributes,
  },
};

/**
 * XSS sanitize — Tiptap HTML output 또는 rawHtml 의 안전 정리 (렌더 직전 적용).
 * Phase 1 acceptance: `<script>alert(1)</script>` 등 인라인 벡터가 inert.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return html;
  return sanitizeHtmlLibrary(stripMalformedDangerousTokens(html), SANITIZE_OPTIONS);
}

/** 공개 렌더링 원장인 HTML에서 검사·검색용 텍스트를 서버에서도 동일하게 파생한다. */
export function htmlToPlainText(html: string): string {
  const safeHtml = sanitizeHtml(html);
  const withBlockBoundaries = safeHtml.replace(
    /<(?:br|hr)\b[^>]*>|<\/(?:blockquote|details|div|figcaption|figure|h[2-4]|li|p|pre|section|summary|table|tbody|td|th|tr)>/gi,
    " ",
  );
  return sanitizeHtmlLibrary(withBlockBoundaries, {
    allowedTags: [],
    allowedAttributes: {},
  })
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * 표준 CSP 헤더 값 (Phase 1 acceptance test #7 정합).
 * Astro middleware / Next.js header config 에서 사용.
 */
export const PHASE_1_CSP =
  "default-src 'self'; " +
  "script-src 'self'; " +
  "style-src 'self' 'unsafe-inline'; " +
  "img-src 'self' data: https://imagedelivery.net https://*.r2.cloudflarestorage.com; " +
  "font-src 'self' data:; " +
  "connect-src 'self'; " +
  // core/embed allowlist (embed.ts) 의 영상 iframe — youtube-nocookie / vimeo.
  "frame-src https://www.youtube-nocookie.com https://www.youtube.com https://player.vimeo.com; " +
  "frame-ancestors 'none'; " +
  "base-uri 'self'; " +
  "form-action 'self'";
